/**
 * A child's device: how it asks to get in, and what it holds afterwards.
 *
 * Nothing here touches Cognito. A child does not have an account, does not
 * have a password and is never asked for an email address -- collecting one
 * would be the single most obvious COPPA problem this product could create.
 *
 * TWO THINGS CHANGED HERE, and they are related.
 *
 * A code no longer buys a working token. It buys a REQUEST, which a parent
 * has to approve; until they do, the token this device holds is refused by
 * the gateway on every route. So `ask` returns something to wait on rather
 * than something to use, and `awaitApproval` is the waiting.
 *
 * And there is no longer ONE session. A family PC has two children on it, and
 * the old single-session store meant the second child's code silently
 * replaced the first's. Sessions are a list keyed by person id now. The
 * KEYPAIR is not: it is per device and shared by every session on it, which
 * is what a parent is really approving when they look at the machine in front
 * of them.
 *
 * WHAT IT DELIBERATELY DOES NOT HOLD is the family key. Each session holds
 * one person's key and a bearer token scoped to that one person. The token's
 * scope is enforced at the gateway by a separate Lambda authorizer, so this
 * device cannot reach a family-wide route even if the page asked -- and
 * without the family key it could not read the answer if it somehow did.
 *
 * The token is a credential and lives in localStorage; the person keys are
 * CryptoKeys in IndexedDB, for the same reason the family key is -- so they
 * are never strings that could land in a log or a crash report.
 */

import { assertKnownOrigin, config } from '../config';
import { deviceKeypair, publicKeyBytes, unwrapFromDevice } from '../crypto/deviceKey';
import { normalise } from '../family/codes';

const SESSIONS_KEY = 'valley.child.sessions';
const DB_NAME = 'valley-child';
const STORE = 'keys';

/**
 * The closed set the server accepts. Never a text field, and now never a
 * question either -- see `detectDeviceKind`.
 */
export const DEVICE_KINDS = ['phone', 'tablet', 'computer', 'other'] as const;
export type DeviceKind = (typeof DEVICE_KINDS)[number];

/**
 * What kind of thing this is, worked out rather than asked.
 *
 * This used to be four buttons. The reason for asking was sound -- the label
 * is what a parent reads when deciding which request to approve, and a text
 * box is where a child types their own name -- but it put a question between
 * a child and their valley to produce an answer the browser already knows.
 *
 * WHAT IT IS ALLOWED TO GET WRONG: quite a lot, and that is the point of it
 * being one word out of four. The label is a hint for a parent looking at a
 * list, not an identifier and not a security control. What actually tells two
 * requests apart is the match code, which is derived from the device's own
 * public key and cannot be guessed or duplicated.
 *
 * NOTHING HERE IS SENT ANYWHERE except the single word it returns. The user
 * agent string is read and discarded in this function; it never leaves the
 * device, and it is not in the request, because a UA string is a fingerprint
 * and this is a child's browser.
 */
export function detectDeviceKind(): DeviceKind {
  const nav = navigator as Navigator & {
    userAgentData?: { mobile?: boolean; platform?: string };
  };
  const ua = navigator.userAgent ?? '';

  // An iPad on iPadOS 13+ claims to be a Macintosh. The touch points are what
  // give it away, and a desktop Mac reports 0.
  if (/Macintosh/.test(ua) && (navigator.maxTouchPoints ?? 0) > 1) return 'tablet';

  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return 'tablet';
  // Android says "Mobile" on phones and omits it on tablets.
  if (/Android/i.test(ua)) return /Mobile/i.test(ua) ? 'phone' : 'tablet';
  if (/iPhone|iPod|Windows Phone|Mobi/i.test(ua)) return 'phone';
  if (/Macintosh|Windows NT|CrOS|X11|Linux/i.test(ua)) return 'computer';

  // The hint browsers offer directly, as a fallback rather than a first
  // resort: it answers "is this mobile", which does not separate the two
  // shapes of mobile the labels care about.
  if (nav.userAgentData?.mobile === true) return 'phone';

  return 'other';
}

export interface ChildSession {
  token: string;
  personId: string;
  key: CryptoKey | null;
}

export interface PendingRequest {
  token: string;
  deviceId: string;
  personId: string;
  /** Shown large on this screen, and on the parent's approval card. */
  match: string;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open storage.'));
  });
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const tx = db.transaction(STORE, mode);
        const request = work(tx.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error ?? new Error('Storage failed.'));
        tx.oncomplete = () => db.close();
      }),
  );
}

interface StoredSession {
  token: string;
  personId: string;
}

function stored(): StoredSession[] {
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (one): one is StoredSession =>
        typeof one === 'object' &&
        one !== null &&
        typeof (one as StoredSession).token === 'string' &&
        typeof (one as StoredSession).personId === 'string',
    );
  } catch {
    return [];
  }
}

function persist(sessions: StoredSession[]): void {
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(sessions));
  } catch {
    // A private window, or blocked site data. This page load still works;
    // the child types their code again next time.
  }
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const url = `${config.apiBase}${path}`;
  // The same guard the parent client uses. A child's tablet is the last place
  // a stray origin should be reachable from.
  assertKnownOrigin(url);

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify(body),
  });
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    throw new Error(payload.error ?? 'That did not work. Ask a grown-up.');
  }
  return payload;
}

/**
 * Type a code, and ask to be let in.
 *
 * Nothing is granted here. What comes back is a token that every child route
 * refuses, plus a match code to show a grown-up. The code itself is reusable
 * and does not expire -- a child on a second computer types the same eight
 * characters, and their parent approves again.
 */
export async function ask(typed: string, kind: DeviceKind): Promise<PendingRequest> {
  const code = normalise(typed, 8);
  const pair = await deviceKeypair();

  return post<PendingRequest>('/v1/link/redeem', {
    code,
    // The public half only. The private key has never been out of IndexedDB
    // and cannot be: it is generated non-extractable.
    devicePub: await publicKeyBytes(pair),
    kind,
  });
}

/**
 * Why the child routes said no, for a device that is still linked.
 *
 * The gateway can only refuse; it cannot say why. Before 2026-10-17 every
 * refusal was read as "a grown-up unlinked this", and the page forgot itself
 * -- so a parent pausing a child, or a family that had not yet given
 * permission (COPPA consent), cost every tablet a fresh approval. This asks
 * the one anonymous route that can tell: `paused` or `consent` mean wait,
 * anything else means the device really is gone.
 */
export async function whyRefused(token: string): Promise<'paused' | 'consent' | 'gone'> {
  try {
    const answer = await post<{ status: string; held?: string }>('/v1/link/status', { token });
    if (answer.status === 'approved' && (answer.held === 'paused' || answer.held === 'consent')) {
      return answer.held;
    }
    return 'gone';
  } catch {
    // Throttled or offline: do not throw a working session away on a guess.
    return 'paused';
  }
}

export type WaitOutcome =
  | { status: 'approved'; session: ChildSession }
  | { status: 'gone' | 'expired' };

/**
 * Ask once whether a grown-up has said yes.
 *
 * Called on a timer by the waiting screen rather than looping here, so the
 * screen can keep showing the match code and stay cancellable.
 *
 * A REFUSED POLL IS NOT AN ANSWER. The route is throttled, and a 429 means
 * "too many people asked at once", not "no". It comes back as `pending` so
 * the screen waits another ten seconds instead of telling a child they were
 * turned down when they were not.
 */
export async function pollApproval(
  request: PendingRequest,
): Promise<WaitOutcome | { status: 'pending' }> {
  let answer: { status: string; keyWrap?: string | null; personId?: string };
  try {
    answer = await post('/v1/link/status', { token: request.token });
  } catch {
    return { status: 'pending' };
  }

  if (answer.status === 'gone' || answer.status === 'expired') {
    return { status: answer.status };
  }
  if (answer.status !== 'approved') return { status: 'pending' };

  const pair = await deviceKeypair();
  let key: CryptoKey | null = null;
  if (answer.keyWrap) {
    try {
      key = await unwrapFromDevice(answer.keyWrap, pair);
    } catch {
      // Wrapped to a keypair this browser no longer has -- cleared site data
      // between asking and being approved. Their balance and their valley
      // still work; only the words are missing.
      key = null;
    }
  }

  const session: ChildSession = {
    token: request.token,
    personId: answer.personId ?? request.personId,
    key,
  };
  await remember(session);
  return { status: 'approved', session };
}

/** Add or replace one person's session, leaving any sibling's alone. */
export async function remember(session: ChildSession): Promise<void> {
  const others = stored().filter((one) => one.personId !== session.personId);
  persist([...others, { token: session.token, personId: session.personId }]);
  if (session.key) {
    try {
      await run('readwrite', (store) => store.put(session.key, session.personId));
    } catch {
      /* as above */
    }
  }
}

/**
 * How many children are signed in on this browser.
 *
 * For the account picker on the PARENT side, which needs to say "and two
 * children use this browser" without becoming a second implementation of the
 * child app. A count, not a list, and no keys are touched: the picker sends
 * people to `/me`, where the existing "Who is this?" screen opens each name
 * with that child's own key, which is the only thing that can.
 *
 * Exported so the storage key stays private to this module. That is the
 * actual principle behind the two surfaces not sharing state -- not that
 * neither may know the other exists, but that neither reaches into the
 * other's storage and makes assumptions about its shape.
 */
export function childSessionCount(): number {
  return stored().length;
}

/** Every session this device holds, with each person's own key. */
export async function restoreAll(): Promise<ChildSession[]> {
  return Promise.all(
    stored().map(async (one) => {
      let key: CryptoKey | null = null;
      try {
        key = (await run<CryptoKey | undefined>('readonly', (s) => s.get(one.personId))) ?? null;
      } catch {
        key = null;
      }
      return { token: one.token, personId: one.personId, key };
    }),
  );
}

/**
 * Drop exactly one person's session.
 *
 * One, not all. A parent unlinking the family PC from Susan's account must
 * not log Ravi out of the same machine, and "I'm done" for one child is not a
 * statement about the other. The device keypair survives both, because it
 * belongs to the browser rather than to either of them.
 */
export async function forgetOne(personId: string): Promise<void> {
  persist(stored().filter((one) => one.personId !== personId));
  try {
    await run('readwrite', (store) => store.delete(personId));
  } catch {
    // Nothing to do. Forgetting must not fail because a delete did.
  }
}
